import React from "react";
import {
  Button,
  FlatActionList,
  FlatActionListItem,
  Page,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { Feather } from "@expo/vector-icons";
import { StackScreenProps } from "@react-navigation/stack";
import { useTranslation } from "react-i18next";
import {
  AccountCountryType,
  UserV2,
} from "@towersystems/roam-common/lib/generated-types";
import { useVendorControllerProvider } from "../../../../utilities/vendor";
import { useMutation } from "@apollo/client";
import { UpdateMeMutation } from "../../../../utilities/active-user/constants";
import { UpdatePasswordFormValues } from "../../components/UpdatePasswordForm/types";
import { useFormContext } from "react-hook-form";
import { UpdatePasswordFormProvider } from "../../components/UpdatePasswordForm/UpdatePasswordFormProvider";
import { UpdatePasswordForm } from "../../components/UpdatePasswordForm";
import { Colors } from "react-native-ui-lib";

export type UpdateCredentialsScreenProps = StackScreenProps<
  any,
  "UpdateCredentialsScreen"
>;

function UpdateCredentialsScreenInner({
  navigation,
}: UpdateCredentialsScreenProps) {
  const { setError, formState, reset, handleSubmit } =
    useFormContext<UpdatePasswordFormValues>();

  const { isDirty, isValid, isSubmitting, isSubmitSuccessful } = formState;
  const disabled = !isValid || !isDirty || isSubmitting;

  const [updateMeMutation] = useMutation<{
    updateMe: UserV2;
  }>(UpdateMeMutation);

  const flashMessagesContext = useFlashMessages();

  const handleOnSubmit = React.useCallback(
    (values: UpdatePasswordFormValues) => {
      return updateMeMutation({
        variables: {
          input: {
            newPassword: values.newPassword,
            currentPassword: values.oldPassword,
          },
        },
      })
        .then((result) => {
          if (result.data?.updateMe) {
            flashMessagesContext.showMessage({
              severity: "success",
              message: "Password updated successfully",
            });
            navigation.goBack();
          } else {
            throw new Error();
          }
        })
        .catch((err) => {
          if (err.message == "error.invalid-credentials") {
            setError("oldPassword", {
              type: "400",
              message: "Invalid password",
            });
          }
        });
    },
    []
  );

  React.useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
    }
  }, [isSubmitSuccessful]);

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={"Update"}
      loading={isSubmitting}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page title="Change Password" footer={footerMarkup}>
      <UpdatePasswordForm onSubmit={handleOnSubmit} />
    </Page>
  );
}

export const UpdateCredentialsScreen = (
  props: UpdateCredentialsScreenProps
) => {
  return (
    <UpdatePasswordFormProvider>
      <UpdateCredentialsScreenInner {...props} />
    </UpdatePasswordFormProvider>
  );
};
